import type { FlightFrame, ShotResult } from '../types';
import { magnitudeVec3 } from '../physics/vec3Math';
import { M_TO_YARDS, RAD_TO_DEG } from '../constants';

/**
 * Compute shot analytics from recorded flight frames.
 */
export function computeShotResult(frames: FlightFrame[]): ShotResult {
  if (frames.length < 2) {
    return emptyResult();
  }

  let maxHeight = 0;
  let apexIndex = 0;
  let landingIndex = frames.length - 1;

  const explicitLanding = frames.findIndex(frame => frame.phase === 'landing');
  // Find apex (max height) and landing point
  // Landing is the first frame after apex where y <= 0 (or near ground)
  for (let i = 0; i <= (explicitLanding >= 0 ? explicitLanding : frames.length - 1); i++) {
    if (frames[i].position.y > maxHeight) {
      maxHeight = frames[i].position.y;
      apexIndex = i;
    }
  }

  // Find landing point: first frame after apex where ball is at or below ground level
  for (let i = Math.max(1, apexIndex); i < frames.length; i++) {
    if (frames[i].position.y <= 0.01) {
      landingIndex = i;
      break;
    }
  }

  if (explicitLanding >= 0) landingIndex = explicitLanding;

  const launch = frames[0];
  const landing = frames[landingIndex];
  const rest = frames[frames.length - 1];

  // Carry distance: 3D distance from launch to landing (ground projection)
  const carryDx = landing.position.x - launch.position.x;
  const carryDz = landing.position.z - launch.position.z;
  const carryDistance = Math.sqrt(carryDx * carryDx + carryDz * carryDz);

  // Total distance: from launch to rest
  const totalDx = rest.position.x - launch.position.x;
  const totalDz = rest.position.z - launch.position.z;
  const totalDistance = Math.sqrt(totalDx * totalDx + totalDz * totalDz);

  // Flight time
  const flightTime = landing.time - launch.time;

  // Landing angle (descent angle at landing)
  const landingSpeed = magnitudeVec3(landing.velocity);
  const landingAngle = landingSpeed > 0.01
    ? Math.atan2(-landing.velocity.y, Math.sqrt(
        landing.velocity.x * landing.velocity.x + landing.velocity.z * landing.velocity.z,
      )) * RAD_TO_DEG
    : 0;

  // Offline: lateral deviation (x component)
  const offline = rest.position.x;

  return {
    carryDistance,
    totalDistance,
    maxHeight,
    flightTime,
    landingAngle: Math.abs(landingAngle),
    landingSpeed,
    offline,
    apexPosition: { ...frames[apexIndex].position },
    landingPosition: { ...landing.position },
    restPosition: { ...rest.position },
  };
}

export function formatShotResult(result: ShotResult): Record<string, string> {
  return {
    carry: (result.carryDistance * M_TO_YARDS).toFixed(1),
    total: (result.totalDistance * M_TO_YARDS).toFixed(1),
    height: (result.maxHeight * M_TO_YARDS).toFixed(1),
    time: result.flightTime.toFixed(2),
    landing: result.landingAngle.toFixed(1),
    offline: (Math.abs(result.offline) * M_TO_YARDS).toFixed(1),
    offlineDir: result.offline >= 0 ? 'R' : 'L',
  };
}

function emptyResult(): ShotResult {
  return {
    carryDistance: 0,
    totalDistance: 0,
    maxHeight: 0,
    flightTime: 0,
    landingAngle: 0,
    landingSpeed: 0,
    offline: 0,
    apexPosition: { x: 0, y: 0, z: 0 },
    landingPosition: { x: 0, y: 0, z: 0 },
    restPosition: { x: 0, y: 0, z: 0 },
  };
}
